// Achats > « À valider » (J1, 02/11/2026) : ce qu'il reste à traiter côté
// fournisseurs — factures d'achat importées encore à valider, et sorties du
// relevé en attente avec la facture proposée, le fournisseur et où agir.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import InvoicePdfButton from "./InvoicePdfButton";
import DocBadge from "./DocBadge";
import { openBlobInNewTab } from "../utils/openBlob";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function PurchasesToValidate({ onOpenOcr, onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => { setError(""); api.purchases.toValidate().then(setData).catch(e => setError(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const openImport = (importId) => {
    try { if (importId) sessionStorage.setItem("execpro360.openImport", String(importId)); } catch { /* facultatif */ }
    onNavigate?.("banque");
  };

  if (error) return <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>;
  const th = { padding: "8px 12px", textAlign: "left", fontWeight: 600 };
  const td = { padding: "8px 12px" };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>À valider</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {data.counts.purchases} facture(s) d'achat à valider · {data.counts.outflows} sortie(s) du relevé en attente, dont {data.counts.outflows_with_proposal} avec une facture proposée
          </div>
        </div>
        <button className="btn" style={{ fontSize: 12 }} onClick={load}>↻ Actualiser</button>
      </div>

      <div className="card" style={{ marginBottom: 14, padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px" }}>
          <div style={{ fontWeight: 700 }}>Factures d'achat à valider ({data.purchases.length})</div>
          {data.purchases.length > 0 && <button className="btn primary" style={{ fontSize: 12 }} onClick={onOpenOcr}>Valider dans « Factures OCR » →</button>}
        </div>
        {data.purchases.length === 0 ? <div style={{ padding: "0 12px 12px", fontSize: 13, color: "var(--text-muted)" }}>Aucune facture d'achat à valider.</div> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
              <th style={th}>Fournisseur</th><th style={th}>Réf. fournisseur</th><th style={th}>Date</th><th style={{ ...th, textAlign: "right" }}>TTC</th>
            </tr></thead>
            <tbody>{data.purchases.map(p => (
              <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                <td style={{ ...td, fontWeight: 600 }}>{p.supplier_name || "Fournisseur non identifié"}</td>
                <td style={{ ...td, fontFamily: "monospace" }}>{p.invoice_ref || "—"}{" "}{p.file_name
                  ? <button className="btn" style={{ fontSize: 11, padding: "1px 6px" }} onClick={() => openBlobInNewTab(() => api.ocrInvoices.file(p.id))} title="Ouvrir le PDF">📎</button>
                  : <span style={{ fontSize: 11, fontWeight: 600, color: "#b45309", fontFamily: "inherit" }}>PDF absent</span>}</td>
                <td style={td}>{fmtDate(p.invoice_date)}</td>
                <td style={{ ...td, textAlign: "right" }}>{p.amount_ttc != null ? `${fmt(p.amount_ttc)} €` : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "10px 12px" }}>
          <div style={{ fontWeight: 700 }}>Sorties du relevé en attente ({data.outflows.length})</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            Pour relier un paiement à sa facture : Comptes bancaires > « 🔎 Proposer les factures », onglet « Paiements fournisseurs ». Les autres sorties (frais, charges sans facture) se valident sur leur compte dans le relevé.
          </div>
        </div>
        {data.outflows.length === 0 ? <div style={{ padding: "0 12px 12px", fontSize: 13, color: "var(--text-muted)" }}>Aucune sortie en attente.</div> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
              <th style={th}>Date</th><th style={th}>Libellé</th><th style={{ ...th, textAlign: "right" }}>Montant</th><th style={th}>Fournisseur</th><th style={th}>Facture proposée</th><th style={th} />
            </tr></thead>
            <tbody>{data.outflows.map(o => (
              <tr key={o.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                <td style={td}>{fmtDate(o.date)}</td>
                <td style={td}>{o.label}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(o.amount)} €</td>
                <td style={td}>{o.supplier_name || <span style={{ color: "var(--text-muted)" }}>non reconnu</span>}</td>
                <td style={td}>
                  {o.proposal ? (
                    <>
                      <DocBadge kind="achat" /><span style={{ fontFamily: "monospace" }}>{o.proposal.supplier_ref || o.proposal.number}</span> <InvoicePdfButton kind="achat" id={o.proposal.purchase_invoice_id} />
                      {o.proposal.supplier_ref && <span style={{ color: "var(--text-muted)", fontSize: 11 }}> (n° interne {o.proposal.number})</span>}
                      <div style={{ fontSize: 11, color: o.proposal.certain ? "#166534" : "#b45309" }}>{o.proposal.certain ? "certaine" : "à vérifier"} — {o.proposal.reason}</div>
                    </>
                  ) : <span style={{ color: "var(--text-muted)" }}>aucune</span>}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => openImport(o.import_id)}>{o.proposal ? "Relier →" : "Ouvrir le relevé →"}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
